import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Heart, MessageCircle, LayoutGrid, LogOut, User as UserIcon, Menu, Megaphone, Dog, Cat, Bird, Turtle, Fish, Rabbit, ShoppingBag, Mail, BadgeCheck, Tractor, HeartHandshake, ChevronDown, Search } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "./ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { RoleAvatar } from "./RoleAvatar";
import { api, photoUrl } from "../lib/api";
import { CATEGORIES } from "../lib/constants";
import { CATEGORY_LABELS_FR } from "../lib/i18n";
import { useLanguage } from "../context/LanguageContext";

const LangToggle = () => {
  const { lang, setLang } = useLanguage();
  return (
    <div className="flex rounded-full bg-slate-100 p-0.5" data-testid="navbar-lang-toggle">
      {["en", "fr"].map((l) => (
        <button
          key={l}
          onClick={() => setLang(l)}
          data-testid={`navbar-lang-${l}`}
          className={`px-2.5 py-1 text-xs font-bold rounded-full transition-colors ${lang === l ? "bg-[#F5551E] text-white" : "text-slate-500 hover:text-[#12234A]"}`}
        >
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  );
};

const CAT_ICONS = { Dog, Cat, Bird, Turtle, Fish, Rabbit, ShoppingBag, Tractor, HeartHandshake, Megaphone, Search };
const NAV_CATEGORIES = CATEGORIES.filter((c) => c.id !== "all");
const MAIN_NAV_COUNT = 6;

// Desktop-only "More" dropdown for the remaining categories (opens on hover or click).
const MoreMenu = ({ items, label }) => {
  const [open, setOpen] = useState(false);
  const { t } = useLanguage();
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (e.type === "keydown" ? e.key === "Escape" : !ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", close);
    return () => { document.removeEventListener("mousedown", close); document.removeEventListener("keydown", close); };
  }, [open]);
  return (
    <div ref={ref} className="relative hidden lg:block" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)} data-testid="nav-more">
      <button type="button" onClick={() => setOpen(true)} aria-expanded={open} data-testid="nav-more-btn"
        className={`flex items-center gap-1 whitespace-nowrap rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${open ? "bg-[#FFF4F0] text-[#F5551E]" : "text-[#12234A] hover:bg-[#FFF4F0] hover:text-[#F5551E]"}`}>
        {t("nav_more")} <ChevronDown size={15} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute left-0 top-full pt-2 z-50" data-testid="nav-more-menu">
          <div className="w-60 bg-white rounded-xl border border-slate-200 shadow-xl p-1.5">
            {items.map((c) => {
              const Icon = CAT_ICONS[c.icon] || ShoppingBag;
              return (
                <Link key={c.id} to={`/browse?category=${c.id}`} onClick={() => setOpen(false)} data-testid={`nav-more-${c.id}`}
                  className="group flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium text-[#12234A] hover:bg-[#FFF4F0] hover:text-[#F5551E] transition-colors">
                  <Icon size={16} className="text-slate-400 group-hover:text-[#F5551E] transition-colors" /> {label(c)}
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const LOGO = "https://customer-assets-jai6qajn.emergentagent.net/job_0bb35e6b-e46a-4892-929f-72cf88d0a5d8/artifacts/jzhk0jln_ChatGPT%20Image%20Sep%2027%2C%202026%2C%2007_56_03%20PM.webp";

// Admin-editable scrolling announcements between the logo and the header buttons.
const TICKER_STYLE_CFG = {
  plain: { wrap: "", text: "text-[#12234A]", accent: "text-[#F5551E]" },
  orange: { wrap: "bg-[#F5551E] rounded-full shadow-sm", text: "text-white", accent: "text-white/70" },
  navy: { wrap: "bg-[#12234A] rounded-full shadow-sm", text: "text-white", accent: "text-[#F5551E]" },
};
const TICKER_SPEED_MS = { slow: "45s", normal: "30s", fast: "16s" };

const trackTickerClick = (id) => { if (id) api.post("/ticker/click", { message_id: id }).catch(() => {}); };

const TickerMsg = ({ m }) => {
  const content = <span className="px-6 font-semibold text-sm whitespace-nowrap">{m.text}</span>;
  if (!m.link) return content;
  return m.link.startsWith("/")
    ? <Link to={m.link} onClick={() => trackTickerClick(m.id)} className="hover:underline">{content}</Link>
    : <a href={m.link} target="_blank" rel="noopener noreferrer" onClick={() => trackTickerClick(m.id)} className="hover:underline">{content}</a>;
};

const HeaderTicker = () => {
  const [ticker, setTicker] = useState(null);
  useEffect(() => { api.get("/ticker").then((r) => setTicker(r.data)).catch(() => {}); }, []);
  if (!ticker || !ticker.active || !(ticker.messages || []).length) return null;

  const cfg = TICKER_STYLE_CFG[ticker.style] || TICKER_STYLE_CFG.plain;
  const duration = TICKER_SPEED_MS[ticker.speed] || TICKER_SPEED_MS.normal;
  const Group = () => (
    <div className="inline-flex items-center shrink-0" aria-hidden="true">
      {ticker.messages.map((m, i) => (
        <span key={i} className="inline-flex items-center">
          <TickerMsg m={m} />
          <span className={`${cfg.accent}`}>✦</span>
        </span>
      ))}
    </div>
  );

  return (
    <div data-testid="header-ticker" className={`ticker-mask flex-1 min-w-0 overflow-hidden hidden md:block mx-2 py-1.5 ${cfg.wrap} ${cfg.text}`}>
      <div className="ticker-track" style={{ animationDuration: duration }}><Group /><Group /></div>
    </div>
  );
};

// Total unread messages for the logged-in user; refreshes on mount + every 30s.
const useUnreadMessages = (enabled) => {
  const [unread, setUnread] = useState(0);
  useEffect(() => {
    if (!enabled) { setUnread(0); return; }
    let active = true;
    const load = () => api.get("/conversations")
      .then((r) => { if (active) setUnread(r.data.reduce((n, c) => n + (c.unread || 0), 0)); })
      .catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => { active = false; clearInterval(t); };
  }, [enabled]);
  return unread;
};

export const Navbar = () => {
  const { user, logout, setAuthOpen } = useAuth();
  const { lang, t } = useLanguage();
  const navigate = useNavigate();
  const unread = useUnreadMessages(!!user);
  const catLabel = (c) => (lang === "fr" ? (CATEGORY_LABELS_FR[c.id] || c.label) : c.label);

  const postAd = () => (user ? navigate("/post") : setAuthOpen(true));

  return (
    <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="h-20 flex items-center gap-2 sm:gap-4">
          <Link to="/" data-testid="navbar-logo-link" className="shrink-0" onClick={() => window.scrollTo({ top: 0, behavior: window.location.pathname === "/" ? "smooth" : "auto" })}>
            <img src={LOGO} alt="CritterPosts.ca" className="h-11 sm:h-[4.5rem] w-auto" />
          </Link>

          <HeaderTicker />

          <div className="flex items-center gap-1.5 sm:gap-2 ml-auto">
            <Button
              onClick={postAd}
              data-testid="navbar-post-ad-button"
              className="gap-1.5 rounded-full bg-[#F5551E] hover:bg-[#E04812] text-white font-semibold h-10 px-4"
            >
              <Plus size={18} /> <span className="hidden sm:inline">{t("nav_post_ad")}</span>
            </Button>

            <LangToggle />

            {user ? (
              <>
                <Link
                  to="/dashboard?tab=messages"
                  data-testid="navbar-messages-icon"
                  aria-label="Messages"
                  className="relative h-10 w-10 rounded-full flex items-center justify-center text-[#12234A] hover:bg-slate-100 transition-colors"
                >
                  <MessageCircle size={22} />
                  {unread > 0 && (
                    <span
                      data-testid="navbar-unread-dot"
                      className="absolute -top-0.5 -right-0.5 min-w-5 h-5 px-1 rounded-full bg-[#F5551E] text-white text-[10px] font-bold flex items-center justify-center border-2 border-white"
                    >
                      {unread > 9 ? "9+" : unread}
                    </span>
                  )}
                </Link>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button data-testid="navbar-user-menu" className="rounded-full outline-none">
                      <RoleAvatar user={user} size={40} />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56 bg-white">
                    <div className="px-2 py-1.5 text-sm">
                      <p className="font-semibold text-[#12234A] truncate">{user.name}</p>
                      <p className="text-slate-400 text-xs truncate">{user.email}</p>
                    </div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="menu-dashboard"><LayoutGrid size={16} className="mr-2" /> {t("nav_dashboard")}</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/dashboard?tab=messages")} data-testid="menu-messages">
                      <MessageCircle size={16} className="mr-2" /> {t("nav_messages")}
                      {unread > 0 && <span className="ml-auto min-w-5 h-5 px-1 rounded-full bg-[#F5551E] text-white text-[10px] font-bold flex items-center justify-center">{unread > 9 ? "9+" : unread}</span>}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/dashboard?tab=favorites")}><Heart size={16} className="mr-2" /> {t("nav_favorites")}</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate(`/seller/${user.user_id}`)}><UserIcon size={16} className="mr-2" /> {t("nav_profile")}</DropdownMenuItem>
                    {(user.role === "admin" || user.is_verified_seller || user.verified_seller || user.verified_business) && (
                      <DropdownMenuItem onClick={() => navigate("/advertise")} data-testid="menu-advertise"><Megaphone size={16} className="mr-2" /> {t("nav_advertise")}</DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={logout} data-testid="menu-logout" className="text-red-600"><LogOut size={16} className="mr-2" /> {t("nav_logout")}</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <Button variant="ghost" onClick={() => setAuthOpen(true)} data-testid="navbar-login-button" className="font-semibold text-[#12234A] h-10 px-2 sm:px-4">
                {t("nav_sign_in")}
              </Button>
            )}
          </div>
        </div>
      </div>

      <nav className="border-t border-slate-100 bg-white/80" data-testid="category-nav">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 sm:gap-2 overflow-x-auto lg:overflow-visible no-scrollbar py-2 flex-1 min-w-0 [mask-image:linear-gradient(to_right,black_92%,transparent)] lg:[mask-image:none]">
              {NAV_CATEGORIES.map((c, i) => {
                const Icon = CAT_ICONS[c.icon] || ShoppingBag;
                return (
                  <Link
                    key={c.id}
                    to={`/browse?category=${c.id}`}
                    data-testid={`nav-category-${c.id}`}
                    className={`${i >= MAIN_NAV_COUNT ? "lg:hidden " : ""}group flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 text-sm font-medium text-[#12234A] hover:bg-[#FFF4F0] hover:text-[#F5551E] transition-colors`}
                  >
                    <Icon size={16} className="text-slate-400 group-hover:text-[#F5551E] transition-colors" />
                    {catLabel(c)}
                  </Link>
                );
              })}
              <MoreMenu items={NAV_CATEGORIES.slice(MAIN_NAV_COUNT)} label={catLabel} />
            </div>
            <div className="shrink-0 flex items-center gap-1 sm:gap-2 pl-1">
              <Link
                to="/get-verified"
                data-testid="nav-get-verified"
                className="group hidden sm:flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 text-sm font-semibold text-[#F5551E] hover:bg-[#FFF4F0] transition-colors"
              >
                <BadgeCheck size={16} className="text-[#F5551E]" />
                {t("nav_get_verified")}
              </Link>
              <Link
                to="/contact"
                data-testid="nav-contact-us"
                className="group flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 text-sm font-semibold text-[#12234A] hover:bg-[#FFF4F0] hover:text-[#F5551E] transition-colors"
              >
                <Mail size={16} className="text-slate-400 group-hover:text-[#F5551E] transition-colors" />
                {t("nav_contact")}
              </Link>
            </div>
          </div>
        </div>
      </nav>
    </header>
  );
};
